import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building2, Briefcase, Calendar, Code } from 'lucide-react';

export const Experience = () => {
  const { t } = useTranslation();

  const experiences = [
    {
      company: t('experience.items.0.company'),
      position: t('experience.items.0.position'),
      period: t('experience.items.0.period'),
      description: t('experience.items.0.description'),
      achievements: [
        t('experience.items.0.achievements.0'),
        t('experience.items.0.achievements.1'),
        t('experience.items.0.achievements.2')
      ],
      techStack: t('experience.items.0.techStack')
    },
    {
      company: t('experience.items.1.company'),
      position: t('experience.items.1.position'),
      period: t('experience.items.1.period'),
      description: t('experience.items.1.description'),
      achievements: [
        t('experience.items.1.achievements.0'),
        t('experience.items.1.achievements.1'),
        t('experience.items.1.achievements.2')
      ],
      techStack: t('experience.items.1.techStack')
    },
    {
      company: t('experience.items.2.company'),
      position: t('experience.items.2.position'),
      period: t('experience.items.2.period'),
      description: t('experience.items.2.description'),
      achievements: [
        t('experience.items.2.achievements.0'),
        t('experience.items.2.achievements.1'),
        t('experience.items.2.achievements.2')
      ],
      techStack: t('experience.items.2.techStack')
    },
    {
      company: t('experience.items.3.company'),
      position: t('experience.items.3.position'),
      period: t('experience.items.3.period'),
      description: t('experience.items.3.description'),
      achievements: [
        t('experience.items.3.achievements.0'),
        t('experience.items.3.achievements.1')
      ],
      techStack: t('experience.items.3.techStack')
    },
    {
      company: t('experience.items.4.company'),
      position: t('experience.items.4.position'),
      period: t('experience.items.4.period'),
      description: t('experience.items.4.description'),
      achievements: [
        t('experience.items.4.achievements.0'),
        t('experience.items.4.achievements.1')
      ],
      techStack: t('experience.items.4.techStack')
    }
  ];

  return (
    <section className="py-20 relative bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl font-semibold text-center mb-16 text-slate-900 dark:text-gray-100"
        >
          {t('experience.title')}
        </motion.h2>

        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 h-full w-px bg-slate-200 dark:bg-gray-700" />

          {experiences.map((exp, index) => (
            <motion.div
              key={exp.company}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
            >
              <div className={`md:text-right ${index % 2 === 0 ? 'md:col-start-1' : 'md:col-start-2'}`}>
                <div className="flex items-center gap-2 mb-2 md:justify-end">
                  <Building2 className="w-5 h-5 text-sky-600 dark:text-cyan-400" />
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100">{exp.company}</h3>
                </div>
                <div className="flex items-center gap-2 mb-2 md:justify-end">
                  <Briefcase className="w-4 h-4 text-slate-500 dark:text-gray-400" />
                  <span className="text-slate-700 dark:text-gray-300">{exp.position}</span>
                </div>
                <div className="flex items-center gap-2 mb-4 md:justify-end">
                  <Calendar className="w-4 h-4 text-slate-500 dark:text-gray-400" />
                  <span className="text-slate-500 dark:text-gray-400">{exp.period}</span>
                </div>
              </div>

              <div className={index % 2 === 0 ? 'md:col-start-2' : 'md:col-start-1 md:row-start-1'}>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-slate-200 dark:border-gray-700 shadow-sm">
                  <p className="text-slate-600 dark:text-gray-300 mb-4">{exp.description}</p>
                  <ul className="space-y-2 mb-4">
                    {exp.achievements.map((achievement, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-gray-300">
                        <span className="text-sky-600 dark:text-cyan-400 mt-1 flex-shrink-0">✓</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-gray-700">
                    <Code className="w-4 h-4 text-sky-600 dark:text-cyan-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-gray-300 font-medium">{t('experience.techStack')}:</span>
                    <span className="text-sm text-slate-600 dark:text-gray-300">{exp.techStack}</span>
                  </div>
                </div>
              </div>

              {/* Timeline dot */}
              <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 w-3 h-3 bg-sky-600 dark:bg-cyan-400 rounded-full border-4 border-white dark:border-gray-900" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};